import React from "react";
import { Box, Chip, Sheet, Stack, Tooltip } from "@mui/joy";
import { useAuth, useEquipment, useRental } from "../../../contexts/api";
import { useConfirmationModal } from "../../../contexts/app/confirmation";
import DarkTypography from "../../common/DarkTypography";
import IconButtonCTA from "../../common/IconButtonCTA";
import CommentsModal from "../../common/CommentsModal";
import { SClose, SComment, SCreate, SDelete, SValidate } from "../../../styles/StyledMUI";
import { colorTokens, textTokens } from "../../../styles/globalStyles";
import { userDisplayName } from "../../../utils/userHelper";
import RentalEquipmentPickerModal from "./RentalEquipmentPickerModal";
import { formatAttributeValue, itemLabel } from "./rentalItemHelpers";
import type {
  Equipment,
  EquipmentAttributeDefinition,
  EquipmentModel,
} from "../../../api/equipment/Equipment.typed";
import type {
  RentalItem,
  RentalModelPricing,
  RentalProduct,
} from "../../../api/rental/Rental.typed";
import type { Comment, MultiComment } from "../../../api/zekay/comments";
import { formatPrice, htToTtc } from "../../billing/shared/billingUtils";

type Props = {
  rentalID: number;
  rentalProduct?: RentalProduct | null;
  isKitMode: boolean;
  unitPricing?: RentalModelPricing[];
  items: RentalItem[];
  canEdit: boolean;
  onChanged: () => void;
  onReturnItem: (
    rentalID: number,
    itemID: number,
    state?: "returned" | "not_returned",
  ) => Promise<unknown>;
  onRemoveItem: (rentalID: number, itemID: number) => Promise<unknown>;
};

function commentsToMulti(list: Comment[]): MultiComment[] {
  return list.map((c) => ({ User: c.user, Comment: c.comment, Date: c.timestamp }));
}

function multiToComments(list: MultiComment[] | undefined): Comment[] {
  return (list || []).map((m) => ({ user: m.User, comment: m.Comment, timestamp: m.Date }));
}

function findCommentsAttribute(model: EquipmentModel | null): EquipmentAttributeDefinition | null {
  return model?.Attributes?.find((a) => a.Type === "comments") ?? null;
}

export type KitSlot = {
  modelID: number;
  modelName: string;
  expected: number;
  got: number;
  unitPriceHT?: number | null;
  taxRate?: number | null;
  currency?: string | null;
};

// Count active (non-returned) items grouped by equipment ModelID.
function countActiveByModel(items: RentalItem[]): Map<number, number> {
  const map = new Map<number, number>();
  for (const it of items) {
    if (it.returned_at) continue;
    const modelID = it.equipment?.ModelID;
    if (modelID == null) continue;
    map.set(modelID, (map.get(modelID) ?? 0) + 1);
  }

  return map;
}

export function buildKitSlots(
  product: RentalProduct | null | undefined,
  items: RentalItem[],
): KitSlot[] {
  const lines = product?.lines ?? [];
  const counts = countActiveByModel(items);

  return lines.map((l) => ({
    modelID: l.equipment_model_id,
    modelName: l.equipment_model?.Name ?? `#${l.equipment_model_id}`,
    expected: l.quantity,
    got: counts.get(l.equipment_model_id) ?? 0,
  }));
}

// Build slots for à-la-carte mode: every priced equipment model becomes a slot
// with no upper bound; existing item counts feed `got` for display purposes only.
export function buildUnitSlots(
  pricing: RentalModelPricing[] | undefined,
  items: RentalItem[],
): KitSlot[] {
  const counts = countActiveByModel(items);
  const known = new Set<number>();
  const slots: KitSlot[] = [];

  if (pricing) {
    for (const row of pricing) {
      if (!row.is_rentable_per_unit || row.rental_price_ht == null) continue;
      known.add(row.model_id);
      slots.push({
        modelID: row.model_id,
        modelName: row.model_name,
        expected: Number.POSITIVE_INFINITY,
        got: counts.get(row.model_id) ?? 0,
        unitPriceHT: row.rental_price_ht,
        taxRate: row.rental_tax_rate,
        currency: row.rental_currency,
      });
    }
  }

  // Surface models already in the basket even if pricing was cleared meanwhile.
  for (const item of items) {
    const modelID = item.equipment?.ModelID;
    if (modelID == null || known.has(modelID)) continue;
    known.add(modelID);
    slots.push({
      modelID,
      modelName: `#${modelID}`,
      expected: Number.POSITIVE_INFINITY,
      got: counts.get(modelID) ?? 0,
      unitPriceHT: item.unit_price_ht ?? null,
      taxRate: item.tax_rate ?? null,
      currency: item.currency ?? null,
    });
  }

  return slots;
}

type LineProps = {
  rentalID: number;
  slot: KitSlot;
  lineItems: RentalItem[];
  canEdit: boolean;
  onChanged: () => void;
  onReturnItem: (
    rentalID: number,
    itemID: number,
    state?: "returned" | "not_returned",
  ) => Promise<unknown>;
  onRemoveItem: (rentalID: number, itemID: number) => Promise<unknown>;
};

function KitLineEditor({
  rentalID,
  slot,
  lineItems,
  canEdit,
  onChanged,
  onReturnItem,
  onRemoveItem,
}: LineProps) {
  const { addRentalItem } = useRental();
  const { getEquipmentModel, updateEquipment } = useEquipment();
  const { user } = useAuth();
  const { showConfirmation } = useConfirmationModal();
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [model, setModel] = React.useState<EquipmentModel | null>(null);
  const [commentsItem, setCommentsItem] = React.useState<RentalItem | null>(null);
  const [commentsAttrID, setCommentsAttrID] = React.useState<string | null>(null);
  const [commentsBuffer, setCommentsBuffer] = React.useState<MultiComment[] | undefined>(undefined);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const fetched = await getEquipmentModel(slot.modelID);
      if (!cancelled) setModel(fetched ?? null);
    })();

    return () => {
      cancelled = true;
    };
  }, [slot.modelID, getEquipmentModel]);

  const commentsAttr = findCommentsAttribute(model);
  const hasCap = Number.isFinite(slot.expected);
  const full = hasCap && slot.got >= slot.expected;

  const handlePicked = async (equipmentID: string): Promise<boolean> => {
    const created = await addRentalItem(rentalID, { equipment_id: equipmentID });
    if (!created) return false;

    onChanged();

    return true;
  };

  const handleReturn = async (item: RentalItem, state: "returned" | "not_returned") => {
    const label = state === "returned" ? "Rendu" : "Non rendu";
    const ok = await showConfirmation(
      label,
      `Confirmer : ${state === "returned" ? "rendre" : "marquer comme non rendu"} « ${itemLabel(item)} » ?`,
    );
    if (!ok) return;
    await onReturnItem(rentalID, item.id, state);
    onChanged();
  };

  const handleRemove = async (item: RentalItem) => {
    const ok = await showConfirmation(
      "Retirer l'équipement",
      `Retirer « ${itemLabel(item)} » du panier ?`,
    );
    if (!ok) return;
    await onRemoveItem(rentalID, item.id);
    onChanged();
  };

  const openCommentsForItem = (item: RentalItem) => {
    if (!commentsAttr || !item.equipment) return;
    const raw = item.equipment.Attributes?.[commentsAttr.ID];
    const existing: Comment[] = Array.isArray(raw) ? raw : [];
    setCommentsItem(item);
    setCommentsAttrID(commentsAttr.ID);
    setCommentsBuffer(commentsToMulti(existing));
  };

  const closeCommentsModal = () => {
    setCommentsItem(null);
    setCommentsAttrID(null);
    setCommentsBuffer(undefined);
  };

  const saveComments = async (updated?: MultiComment[]) => {
    if (!commentsItem?.equipment || !commentsAttrID) return;
    const next: Equipment = {
      ...commentsItem.equipment,
      Attributes: {
        ...(commentsItem.equipment.Attributes || {}),
        [commentsAttrID]: multiToComments(updated),
      },
    };
    await updateEquipment(next);
    closeCommentsModal();
    onChanged();
  };

  return (
    <Sheet
      sx={{
        backgroundColor: colorTokens.bg.surfaceMuted,
        border: `1px solid ${colorTokens.border.default}`,
        borderRadius: "8px",
        p: 1,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mb: lineItems.length > 0 || (canEdit && !full) ? 0.75 : 0 }}
      >
        <DarkTypography level="body-sm" sx={{ fontWeight: 600, flex: 1 }}>
          {slot.modelName}
        </DarkTypography>
        {slot.unitPriceHT != null && slot.taxRate != null && (
          <Chip size="sm" variant="soft" color="primary">
            {formatPrice(htToTtc(slot.unitPriceHT, slot.taxRate), slot.currency ?? "EUR")}/jour
          </Chip>
        )}
        {hasCap ? (
          <Chip size="sm" variant="soft" color={full ? "success" : "warning"}>
            {slot.got}/{slot.expected}
          </Chip>
        ) : (
          slot.got > 0 && (
            <Chip size="sm" variant="soft" color="neutral">
              ×{slot.got}
            </Chip>
          )
        )}
      </Stack>

      {lineItems.length > 0 && (
        <Stack spacing={0.5} sx={{ mb: canEdit && !full ? 0.75 : 0 }}>
          {lineItems.map((item) => {
            const isClosed = !!item.returned_at;
            const state = item.return_state;
            const eq = item.equipment;
            const attrs = (model?.Attributes ?? []).filter(
              (a) => formatAttributeValue(a, eq?.Attributes?.[a.ID]) !== "",
            );

            return (
              <Box
                key={item.id}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 0.5,
                  px: 1,
                  py: 0.5,
                  borderRadius: "sm",
                  backgroundColor: colorTokens.bg.surface,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                    <DarkTypography
                      level="body-sm"
                      sx={{
                        fontWeight: 600,
                        textDecoration: isClosed ? "line-through" : "none",
                        color: isClosed ? textTokens.color.dimmed : undefined,
                      }}
                    >
                      {itemLabel(item)}
                    </DarkTypography>
                    {eq?.ID && (
                      <DarkTypography level="body-xs" colorVariant="muted">
                        {eq.ID.slice(0, 8)}
                      </DarkTypography>
                    )}
                    {isClosed && (
                      <Chip
                        size="sm"
                        variant="soft"
                        color={state === "not_returned" ? "danger" : "success"}
                      >
                        {state === "not_returned" ? "Non rendu" : "Rendu"}
                      </Chip>
                    )}
                  </Stack>
                  {attrs.length > 0 && (
                    <Box
                      sx={{
                        mt: 0.5,
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "repeat(2, minmax(0, 1fr))",
                          md: "repeat(3, minmax(0, 1fr))",
                        },
                        columnGap: 1,
                        rowGap: 0.25,
                      }}
                    >
                      {attrs.map((a) => (
                        <Box key={a.ID} sx={{ minWidth: 0 }}>
                          <DarkTypography
                            level="body-xs"
                            colorVariant="muted"
                            sx={{
                              textTransform: "uppercase",
                              letterSpacing: "0.3px",
                              fontSize: "0.6rem",
                            }}
                          >
                            {a.Label}
                          </DarkTypography>
                          <DarkTypography
                            level="body-xs"
                            sx={{
                              color: textTokens.color.primary,
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {formatAttributeValue(a, eq?.Attributes?.[a.ID])}
                          </DarkTypography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
                <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
                  {commentsAttr && eq && (
                    <Tooltip title={commentsAttr.Label}>
                      <IconButtonCTA
                        aria-label={`Ouvrir ${commentsAttr.Label}`}
                        size="sm"
                        ctaVariant="accent"
                        onClick={() => openCommentsForItem(item)}
                      >
                        <SComment />
                      </IconButtonCTA>
                    </Tooltip>
                  )}
                  {canEdit && !isClosed && (
                    <Tooltip title="Rendu">
                      <IconButtonCTA
                        aria-label="Marquer rendu"
                        size="sm"
                        ctaVariant="success"
                        onClick={() => handleReturn(item, "returned")}
                      >
                        <SValidate />
                      </IconButtonCTA>
                    </Tooltip>
                  )}
                  {canEdit && !isClosed && (
                    <Tooltip title="Non rendu">
                      <IconButtonCTA
                        aria-label="Marquer non rendu"
                        size="sm"
                        ctaVariant="danger"
                        onClick={() => handleReturn(item, "not_returned")}
                      >
                        <SClose />
                      </IconButtonCTA>
                    </Tooltip>
                  )}
                  {canEdit && !isClosed && (
                    <Tooltip title="Retirer du panier">
                      <IconButtonCTA
                        aria-label="Retirer du panier"
                        size="sm"
                        ctaVariant="danger"
                        onClick={() => handleRemove(item)}
                      >
                        <SDelete />
                      </IconButtonCTA>
                    </Tooltip>
                  )}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      )}

      {canEdit && !full && (
        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
          <IconButtonCTA
            aria-label="Ajouter un équipement"
            size="sm"
            ctaVariant="accent"
            onClick={() => setPickerOpen(true)}
          >
            <SCreate />
          </IconButtonCTA>
        </Stack>
      )}

      <RentalEquipmentPickerModal
        open={pickerOpen}
        modelID={slot.modelID}
        modelName={slot.modelName}
        onClose={() => setPickerOpen(false)}
        onPicked={handlePicked}
      />

      <CommentsModal
        open={commentsItem != null}
        title={
          commentsAttr && commentsItem ? `${commentsAttr.Label} — ${itemLabel(commentsItem)}` : ""
        }
        currentUserName={userDisplayName(user)}
        multiComments={commentsBuffer}
        setMultiComments={setCommentsBuffer}
        onClose={closeCommentsModal}
        allowEdit={canEdit}
        onSave={saveComments}
      />
    </Sheet>
  );
}

export default function RentalAddItemBlock({
  rentalID,
  rentalProduct,
  isKitMode,
  unitPricing,
  items,
  canEdit,
  onChanged,
  onReturnItem,
  onRemoveItem,
}: Props) {
  const slots = React.useMemo(
    () => (isKitMode ? buildKitSlots(rentalProduct, items) : buildUnitSlots(unitPricing, items)),
    [isKitMode, rentalProduct, unitPricing, items],
  );

  if (slots.length === 0) {
    return (
      <DarkTypography level="body-sm" sx={{ color: textTokens.color.dimmed }}>
        {isKitMode
          ? "Kit sans composition définie."
          : "Aucun modèle d'équipement avec tarif unitaire. Définir un tarif dans Produits › Location."}
      </DarkTypography>
    );
  }

  // Items not bound to a kit model (e.g. legacy data) are surfaced separately.
  const knownModelIDs = new Set(slots.map((s) => s.modelID));
  const itemsByModel = new Map<number, RentalItem[]>();
  const orphanItems: RentalItem[] = [];

  for (const it of items) {
    const modelID = it.equipment?.ModelID;
    if (modelID != null && knownModelIDs.has(modelID)) {
      const list = itemsByModel.get(modelID) ?? [];
      list.push(it);
      itemsByModel.set(modelID, list);
    } else {
      orphanItems.push(it);
    }
  }

  return (
    <Stack spacing={1}>
      {slots.map((slot) => (
        <KitLineEditor
          key={slot.modelID}
          rentalID={rentalID}
          slot={slot}
          lineItems={itemsByModel.get(slot.modelID) ?? []}
          canEdit={canEdit}
          onChanged={onChanged}
          onReturnItem={onReturnItem}
          onRemoveItem={onRemoveItem}
        />
      ))}

      {orphanItems.length > 0 && (
        <Sheet
          sx={{
            backgroundColor: colorTokens.bg.surfaceMuted,
            border: `1px solid ${colorTokens.border.default}`,
            borderRadius: "8px",
            p: 1,
          }}
        >
          <DarkTypography level="body-xs" colorVariant="muted" sx={{ mb: 0.5 }}>
            Hors kit
          </DarkTypography>
          <Stack spacing={0.25}>
            {orphanItems.map((item) => (
              <Box
                key={item.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1,
                  py: 0.25,
                  borderRadius: "sm",
                  backgroundColor: colorTokens.bg.surface,
                }}
              >
                <DarkTypography level="body-sm" sx={{ flex: 1 }}>
                  {itemLabel(item)}
                </DarkTypography>
              </Box>
            ))}
          </Stack>
        </Sheet>
      )}
    </Stack>
  );
}
